import { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Stack } from '@mui/material';
import {
  CheckboxInput,
  InfiniteList,
  ListCard,
  ListTotal,
  TableToolbar,
  TableWrapper,
  Typography,
} from 'melp-design/components';
import { APP } from 'config';
import moment from 'moment';
import { shopItemCategories } from 'types/MarketplaceItems';
import {
  DiscountsFilter,
  FilterButton,
  Filters,
  SelectFilter,
} from 'components/filters';
import {
  availabilityStatuses,
  discountOfferSummary,
  discountsFiltersSchema,
  useBulkUpdateDiscounts,
  useInfiniteDiscounts,
} from 'store/discounts';
import { useCompanyLanguages } from 'state/Administrators';
import { resolveTranslation } from 'utils/general';
import { usePredefinedToasts } from 'utils/Toast';
import { useModalContext } from 'store/modal';
import { formatCountry } from 'utils/format';
import { useSearchParams } from 'utils/navigation';
import { DiscountDetailsModal } from './atoms';

export const DiscountsList: FC = () => {
  const { t, i18n } = useTranslation();
  const { defaultLanguage } = useCompanyLanguages();
  const predefinedToasts = usePredefinedToasts();
  const { openModal } = useModalContext();

  const [selected, setSelected] = useState<string[]>([]);

  const { searchParams: filters, navigate } = useSearchParams(
    discountsFiltersSchema,
  );

  const { data, fetchNextPage, hasNextPage, isLoading } =
    useInfiniteDiscounts(filters);

  const { mutate: bulkUpdate, isLoading: isUpdating } =
    useBulkUpdateDiscounts();

  const discounts = useMemo(() => {
    return {
      items: (data?.pages.map(({ items }) => items) ?? []).flat(),
      total: data?.pages[0]?.total ?? 0,
    };
  }, [data]);

  return (
    <TableWrapper>
      <TableToolbar>
        <Stack gap={1}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            gap={1}
          >
            <ListTotal total={discounts?.total} />

            <Stack direction="row" alignItems="center" gap={1}>
              <Filters
                value={filters}
                initialValues={discountsFiltersSchema.parse({})}
                onChange={(v) => navigate({ params: { ...v, page: 1 } })}
              >
                {({ value, initialValues, setFilter }) => (
                  <>
                    <FilterButton
                      label={t('common.status')}
                      value={value.status}
                      initialValue={initialValues.status}
                      onChange={(v) => setFilter('status', v)}
                    >
                      {({ value, applyFilter, clearFilter, close }) => (
                        <SelectFilter
                          value={{ value }}
                          onApplyFilter={({ value }) => applyFilter(value)}
                          onClearFilter={clearFilter}
                          onCancel={close}
                          options={availabilityStatuses.map((type) => ({
                            key: type,
                            label: t(`status.${type}`),
                          }))}
                          multiple
                          searchable={false}
                        />
                      )}
                    </FilterButton>

                    <FilterButton
                      label={t('benefits.country')}
                      value={value.countries}
                      initialValue={initialValues.countries}
                      onChange={(v) => setFilter('countries', v)}
                    >
                      {({ value, applyFilter, clearFilter, close }) => (
                        <SelectFilter
                          name="countries"
                          value={{ value }}
                          onApplyFilter={({ value }) => applyFilter(value)}
                          onClearFilter={clearFilter}
                          onCancel={close}
                          multiple
                          options={APP.countries.alpha3.map((alpha3) => ({
                            key: alpha3,
                            label: formatCountry(i18n.language, alpha3),
                          }))}
                        />
                      )}
                    </FilterButton>

                    <FilterButton
                      label={t('benefits.benefit_type')}
                      value={value.categories}
                      initialValue={initialValues.categories}
                      onChange={(v) => setFilter('categories', v)}
                    >
                      {({ value, applyFilter, clearFilter, close }) => (
                        <SelectFilter
                          name="categories"
                          value={{ value }}
                          onApplyFilter={({ value }) => applyFilter(value)}
                          onClearFilter={clearFilter}
                          onCancel={close}
                          multiple
                          options={shopItemCategories.map((category) => ({
                            key: category,
                            label: t(
                              `marketplaceItems.itemCategory-${category}`,
                            ),
                          }))}
                        />
                      )}
                    </FilterButton>

                    <FilterButton
                      label={t('marketplaceItems.discountPercentage')}
                      value={value.discounts}
                      initialValue={initialValues.discounts}
                      onChange={(v) => setFilter('discounts', v)}
                    >
                      {({ value, applyFilter, clearFilter, close }) => (
                        <DiscountsFilter
                          value={{ value }}
                          onApplyFilter={({ value }) => applyFilter(value)}
                          onClearFilter={clearFilter}
                          onCancel={close}
                        />
                      )}
                    </FilterButton>
                  </>
                )}
              </Filters>
            </Stack>
          </Stack>
        </Stack>
      </TableToolbar>

      <TableWrapper.InfiniteListWrapper>
        <InfiniteList
          data={discounts.items}
          renderItem={(item) => {
            const translation = resolveTranslation(
              item.translations,
              i18n.language,
              defaultLanguage,
            );
            const offer = discountOfferSummary(
              i18n.language,
              item,
              t('discounts.offerType.SPECIAL_OFFER'),
            );

            return (
              <ListCard
                title={translation?.title}
                description={translation?.teaser}
                image={item.image?.url}
                logo={item.supplier?.logo?.url}
                logoTooltip={item.supplier?.logo?.name}
                label={translation?.promo}
                tags={[
                  {
                    tag: t('common.new'),
                    color: 'success',
                    visible: moment(item.publishedAt).isAfter(
                      moment().subtract(2, 'weeks'),
                    ),
                  },
                  {
                    tag: t('discounts.sponsored'),
                    color: 'textSecondary',
                    visible: item.priority <= 10,
                  },
                  {
                    tag: offer ?? '',
                    color: 'textSecondary',
                    visible: Boolean(offer),
                  },
                ]}
                onClick={() => {
                  openModal({
                    component: DiscountDetailsModal,
                    size: 'lg',
                    props: { discount: item },
                  });
                }}
                additional={
                  <Stack
                    flexDirection="row"
                    gap={1}
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <CheckboxInput
                      checked={selected.includes(item.id)}
                      onChange={(checked) =>
                        setSelected((prev) =>
                          checked
                            ? [...prev, item.id]
                            : prev.filter((id) => id !== item.id),
                        )
                      }
                    />
                    <Typography color={item.isEnabled ? 'success' : 'error'}>
                      {item.isEnabled
                        ? t('status.enabled')
                        : t('status.disabled')}
                    </Typography>
                  </Stack>
                }
              />
            );
          }}
          fetchNextPage={fetchNextPage}
          hasNextPage={hasNextPage}
          isLoading={isLoading}
          selection={{
            value: selected,
            onClear: () => setSelected([]),
            actions: [
              {
                label: t('discounts.actions.enable'),
                disabled: isUpdating,
                onClick: () =>
                  bulkUpdate(
                    { enabled: selected },
                    {
                      onSuccess: () => {
                        predefinedToasts.success.updated();
                        setSelected([]);
                      },
                    },
                  ),
              },
              {
                label: t('discounts.actions.disable'),
                disabled: isUpdating,
                onClick: () =>
                  bulkUpdate(
                    { disabled: selected },
                    {
                      onSuccess: () => {
                        predefinedToasts.success.updated();
                        setSelected([]);
                      },
                    },
                  ),
              },
            ],
          }}
        />
      </TableWrapper.InfiniteListWrapper>
    </TableWrapper>
  );
};
